/**
 * ToFilevineSuggestionCard - Card for TO_FILEVINE direction suggestions
 *
 * Shows a suggestion to push data from Finch to Filevine.
 * Features:
 * - Entity pair display (Finch → Filevine)
 * - Field changes table with rich Filevine editors
 * - Accept/Reject actions
 * - Edit modal with FilevineEntityDetails
 */

'use client';

import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import React, { forwardRef } from 'react';

import { cn } from '@/lib/utils';
import type { FilevineSectionFieldConfigSchema } from '@/services/api/generated/entitySyncService';

import { FilevineSectionFieldValue } from '../../shared/FilevineEntityDetails';
import { SuggestedValueDisplay } from '../../shared/LabeledField';
import { parseSuggestedValue } from '../../utils/fieldMatching';
import { FieldValue } from '../../utils/fieldRenderers';
import { getFieldValue } from '../../utils/fieldUtils';
import { FilevineEntityCard } from '../FilevineEntityCard';
import { FinchEntityCard } from '../finch-cards';
import {
  ConfidenceBadge,
  SuggestionCardActions,
} from './SuggestionCardShared';
import { SuggestionSetDetailsSheet } from './SuggestionSetDetailsSheet';
import type { FieldUpdate, ToFilevineSuggestionCardProps } from './types';
import { useToFilevineSuggestionCard } from './useToFilevineSuggestionCard';

// ============================================================================
// Helpers
// ============================================================================

function findFieldConfigForKey(
  key: string,
  fieldConfigs: FilevineSectionFieldConfigSchema[],
): FilevineSectionFieldConfigSchema | undefined {
  const lowerKey = key.toLowerCase();

  return fieldConfigs.find((fc) => {
    if (fc.internal_name === key) return true;
    if (fc.internal_name.toLowerCase() === lowerKey) return true;

    const selectorParts = fc.field_selector.split('/');
    const fieldName = selectorParts[selectorParts.length - 1];
    if (fieldName === key || fieldName.toLowerCase() === lowerKey) return true;

    const lowerInternal = fc.internal_name.toLowerCase();
    if (
      lowerInternal.startsWith(lowerKey) &&
      lowerInternal !== lowerKey &&
      /^\d+$/.test(lowerInternal.slice(lowerKey.length))
    ) {
      return true;
    }

    return false;
  });
}

function formatFieldKey(key: string): string {
  const withoutSuffix = key.replace(/\d+$/, '');
  return withoutSuffix
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase());
}

// ============================================================================
// Sub-components
// ============================================================================

const CurrentValueCell: React.FC<{
  value: unknown;
  fieldType: number;
  hasConfig: boolean;
  isCreate: boolean;
}> = ({ value, fieldType, hasConfig, isCreate }) => {
  if (isCreate) {
    return <span className="text-zinc-400 italic">(none)</span>;
  }
  if (hasConfig) {
    return <FieldValue value={value} fieldType={fieldType} />;
  }
  return (
    <span className="text-zinc-600">{parseSuggestedValue(value).text}</span>
  );
};

const NewValueCell: React.FC<{
  value: unknown;
  fieldType?: number;
  isClear: boolean;
  firmId?: string;
}> = ({ value, fieldType, isClear, firmId }) => {
  if (isClear) {
    return <span className="text-zinc-400 italic">(clear)</span>;
  }

  const parsed = parseSuggestedValue(value);

  if (parsed.type === 'contact_link' || parsed.type === 'contact_create') {
    return <SuggestedValueDisplay parsed={parsed} firmId={firmId} />;
  }

  if (fieldType !== undefined) {
    return <FieldValue value={value} fieldType={fieldType} />;
  }

  return <SuggestedValueDisplay parsed={parsed} firmId={firmId} />;
};

// ============================================================================
// Main Component
// ============================================================================

export const ToFilevineSuggestionCard = forwardRef<
  HTMLDivElement,
  ToFilevineSuggestionCardProps
>(function ToFilevineSuggestionCard(
  {
    suggestion,
    parentSet,
    finchEntity,
    filevineEntity,
    fieldConfigs,
    sectionConfigData,
    canonicalSectionType,
    firmId,
    onAccept,
    onReject,
    index,
  },
  ref,
) {
  const card = useToFilevineSuggestionCard({
    suggestion,
    parentSet,
    onAccept,
    onReject,
  });

  const isCreate = suggestion.action_type === 'CREATE';
  const withReview = suggestion as typeof suggestion & {
    is_auto_pushed?: boolean;
    review_status?: string | null;
  };
  const isAutoPushReview =
    withReview.is_auto_pushed === true &&
    suggestion.status === 'COMPLETED' &&
    withReview.review_status === 'PENDING_REVIEW';

  const handleFilevinePayloadChange = (
    fieldName: string,
    value: FilevineSectionFieldValue,
  ) => {
    card.updateField(fieldName, value);
  };

  // Determine status-based styling
  const isPending = suggestion.status === 'PENDING';
  const isProcessing =
    suggestion.status === 'ACCEPTED' || suggestion.status === 'EXECUTING';
  const isCompleted = suggestion.status === 'COMPLETED';
  const isFailed = suggestion.status === 'FAILED';

  // Accent line color based on status
  const accentLineClass = cn(
    'absolute top-0 left-0 right-0 h-1 bg-gradient-to-r',
    isPending && 'from-blue-500 to-indigo-500',
    isProcessing && 'from-amber-400 to-amber-500',
    isCompleted && 'from-emerald-500 to-teal-500',
    isFailed && 'from-rose-500 to-red-500',
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="group relative"
    >
      <div
        className={cn(
          'relative bg-white rounded-2xl border shadow-sm transition-all duration-200 overflow-hidden',
          isPending && 'border-zinc-200 hover:shadow-md',
          isProcessing && 'border-amber-200 bg-amber-50/30',
          isCompleted && 'border-emerald-200 bg-emerald-50/30',
          isFailed && 'border-rose-200 bg-rose-50/30',
        )}
      >
        {/* Accent line - color changes based on status */}
        <div className={accentLineClass} />

        {/* Entity Cards Section */}
        <div className="p-4 pt-5">
          <div className="flex items-stretch gap-4">
            {/* Finch Entity Card (source) */}
            <div className="flex-1 min-w-0 flex">
              {finchEntity ? (
                <FinchEntityCard
                  entity={finchEntity}
                  canonicalSectionType={canonicalSectionType}
                  matterId={parentSet.matter_id}
                  compact
                  className="flex-1"
                />
              ) : (
                <div className="flex-1 rounded-lg border border-dashed border-zinc-200 p-3 text-center text-sm text-zinc-400 flex items-center justify-center">
                  No Finch entity
                </div>
              )}
            </div>

            {/* Direction indicator */}
            <div className="flex flex-col items-center justify-center gap-1">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                <ArrowUpRight className="h-4 w-4 text-blue-600" />
              </div>
              <span className="text-xs font-medium text-blue-600">
                {filevineEntity ? 'Update' : 'Create'}
              </span>
            </div>

            {/* Filevine Entity Card (destination) */}
            <div className="flex-1 min-w-0 flex">
              {filevineEntity ? (
                <FilevineEntityCard
                  entity={filevineEntity}
                  fieldConfigs={fieldConfigs}
                  sectionConfigData={sectionConfigData}
                  compact
                  className="flex-1"
                />
              ) : (
                <div className="flex-1 rounded-lg border border-dashed border-zinc-200 p-3 text-center text-sm text-zinc-400 flex items-center justify-center">
                  Will create in Filevine
                </div>
              )}
            </div>

            {/* Confidence badge */}
            {/* Confidence badge */}
            <ConfidenceBadge
              confidence={card.confidence}
              aiReasoning={card.aiReasoning}
            />
          </div>
        </div>

        {/* Changes section */}
        <div className="border-t border-zinc-100">
          <button
            type="button"
            onClick={card.toggleExpand}
            className="w-full px-5 py-3 flex items-center justify-between text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors"
          >
            {card.isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          <AnimatePresence>
            {card.isExpanded && card.fieldUpdates.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-4">
                  <div className="rounded-xl border border-zinc-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-zinc-50 text-left">
                          <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                            Field
                          </th>
                          <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                            Current
                          </th>
                          <th className="w-10 px-2 py-2.5" />
                          <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                            New
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {card.fieldUpdates.map((update: FieldUpdate) => {
                          const matchingConfig = findFieldConfigForKey(
                            update.fieldKey,
                            fieldConfigs,
                          );
                          const fieldLabel = matchingConfig
                            ? matchingConfig.name
                            : formatFieldKey(update.fieldKey);
                          const fieldType =
                            matchingConfig?.custom_field_type ?? 0;

                          const currentValue =
                            isCreate || !matchingConfig
                              ? null
                              : getFieldValue(
                                  filevineEntity ?? undefined,
                                  matchingConfig,
                                );

                          return (
                            <tr
                              key={update.fieldKey}
                              className="transition-colors bg-white"
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <ArrowUpRight className="w-4 h-4 text-blue-500" />
                                  <span className="font-medium text-zinc-700">
                                    {fieldLabel}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div
                                  className={cn(
                                    !update.isClear
                                      ? 'opacity-50 line-through'
                                      : '',
                                  )}
                                >
                                  <CurrentValueCell
                                    value={currentValue}
                                    fieldType={fieldType}
                                    hasConfig={Boolean(matchingConfig)}
                                    isCreate={isCreate}
                                  />
                                </div>
                              </td>
                              <td className="px-2 py-3 text-center">
                                <ArrowRight className="w-4 h-4 text-zinc-300 inline-block" />
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-zinc-700">
                                  <NewValueCell
                                    value={update.newValue}
                                    fieldType={
                                      matchingConfig?.custom_field_type
                                    }
                                    isClear={update.isClear}
                                    firmId={firmId}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty state for no field updates */}
          {card.isExpanded && card.fieldUpdates.length === 0 && (
            <div className="px-5 pb-4">
              <div className="rounded-xl border border-zinc-200 p-4 text-center text-sm text-zinc-500">
                {isCreate
                  ? 'Create new record in Filevine'
                  : 'Update record in Filevine'}
              </div>
            </div>
          )}
        </div>

        {/* Actions - changes based on suggestion status */}
        <SuggestionCardActions
          status={suggestion.status}
          isCreate={isCreate}
          isAutoPushReview={isAutoPushReview}
          autoPushReviewStatus={
            withReview.is_auto_pushed ? withReview.review_status : null
          }
          hasEdits={card.isPayloadModified}
          destinationLabel="Filevine"
          accentColor="blue"
          card={card}
        />
      </div>

      {/* Edit Modal */}
      <SuggestionSetDetailsSheet
        suggestionSet={parentSet}
        open={card.isEditModalOpen}
        onOpenChange={card.setEditModalOpen}
        finchEntity={finchEntity}
        filevineEntity={filevineEntity}
        fieldConfigs={fieldConfigs}
        firmId={firmId}
        onFilevinePayloadChange={handleFilevinePayloadChange}
        modifiedFilevinePayload={card.editedPayload}
        editable
        direction="TO_FILEVINE"
      />
    </motion.div>
  );
});

export default ToFilevineSuggestionCard;
